/**
 * @file CompilationGrid.tsx
 */

import s from './CompilationGrid.module.sass'

import { FC, ReactElement } from 'react'

import { CompilationGridSkeleton } from '@widgets/News/CompilationGrid/index'
import { NewsCardCompilation } from '@widgets/News/NewsCardCompilation'
import { SelectionCardCompilation } from '@widgets/Selection/SelectionCardCompilation'

import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { TypeMappingSelectionOutput } from '@shared/api/middleware/mappingAPI/selection/type'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { useCurrentSize } from '@shared/lib/helpers/classes'
import type { TypeNewsCardData } from '@shared/api/middleware/mappingAPI/news/viewModel'

import {
  compilationGridTypeNews,
  compilationGridTypeSelections,
  type BuildCompilationGridCardPropsParams,
  type Props,
  type RenderCardCardProps,
  type RenderCardParams,
} from './CompilationGrid.types'

/** соотношение countsNumber + listLength → схема размеров карточек */
const SCHEMA_CONFIG: Record<string, Array<[maxLength: number, schema: string[]]>> = {
  '6': [
    [2, ['D2x1', 'D2x1']],
    [3, ['D1x1', 'D1x1', 'D2x1']],
    [4, ['D1x1', 'D1x1', 'D1x1', 'D1x1']],
    [5, ['D1x1', 'D1x1', 'D2x1', 'D2x1', 'D2x1']],
    [Infinity, ['D1x1', 'D1x1', 'D2x1', 'D2x1', 'D1x1', 'D1x1']],
  ],
  '4': [
    [1, ['D2x1']],
    [2, ['D1x1', 'D2x1']],
    [3, ['D1x1', 'D1x1', 'D1x1']],
    [Infinity, ['D1x1', 'D2x1', 'D2x1', 'D1x1']],
  ],
}

/** метка «длинной» ячейки в SCHEMA_CONFIG → span 2 в гриде */
const compilationGridLongCardSize = 'D2x1' as const

const getSizeCard = (index: number, countsNumber: string, listLength: number): string | null => {
  const entries = SCHEMA_CONFIG[countsNumber]
  const match = entries?.find(([max]) => listLength <= max)
  const schema = match?.[1] ?? null
  return schema?.[index] ?? null
}

/** @function - cardProps для одной ячейки сетки; null если индекс за пределами visibleCount */
const buildCompilationGridCardProps = (p: BuildCompilationGridCardPropsParams): RenderCardCardProps | null => {
  if (p.index >= p.visibleCount) return null

  const size = getSizeCard(p.index, p.countsMode, p.listLength)
  const isLongSize = size === compilationGridLongCardSize

  return {
    style: isLongSize ? ({ gridColumn: 'span 2', minWidth: 'initial' } as const) : undefined,
    sizes: size,
    className: s.item,
    analytic: {
      section_name: p.analytic.section_name,
      section_index: p.analytic.analytic_indexes ? p.analytic.analytic_indexes[p.index] : p.index,
    },
  }
}

/** @function - рендерит карточку подборки */
const renderSelectionCard = (item: TypeMappingSelectionOutput, cardProps: RenderCardCardProps) => (
  <SelectionCardCompilation key={item.id} {...cardProps} data={item} />
)

/** @function - рендерит карточку новости */
const renderNewsCard = (item: TypeNewsCardData, cardProps: RenderCardCardProps) => (
  <NewsCardCompilation key={item.id} {...cardProps} data={item} />
)

/** @function renderCard - рендерит карточку по типу (selections или news) */
const renderCard = (p: RenderCardParams): ReactElement => {
  return p.type === compilationGridTypeSelections
    ? renderSelectionCard(p.item, p.cardProps)
    : renderNewsCard(p.item, p.cardProps)
}

/**
 * @function CompilationGrid - компонент сетки подборок
 * @description Компонент сетки подборок для отображения списка элементов в виде сетки.
 */
const CompilationGrid: FC<Props> = ({ list, type, counts = '6 4 4', analytic, className = '', skeleton = false }) => {
  const [countsCurrentBreakpoint] = useCurrentSize(counts + ' -')
  const { isMobile } = useClientResponsive()

  const gridVisibleCount = Number(countsCurrentBreakpoint)
  const gridCountsMode = String(countsCurrentBreakpoint)

  const variants = [
    !isMobile && skeleton && <CompilationGridSkeleton counts={counts} className={className} />,

    !isMobile && !skeleton && (
      <BlockBoundary>
        <div className={`${s.grid} ${className}`} data-mode={countsCurrentBreakpoint} data-elems-length={list.length}>
          {list.map((item, index) => {
            const cardProps = buildCompilationGridCardProps({
              index,
              visibleCount: gridVisibleCount,
              countsMode: gridCountsMode,
              listLength: list.length,
              analytic,
            })

            if (!cardProps) return null

            return type === compilationGridTypeSelections
              ? renderCard({
                  type: compilationGridTypeSelections,
                  item: item as TypeMappingSelectionOutput,
                  cardProps,
                })
              : renderCard({ type: compilationGridTypeNews, item: item as TypeNewsCardData, cardProps })
          })}
        </div>
      </BlockBoundary>
    ),
  ]

  return variants.find(Boolean) ?? null
}

export default CompilationGrid
